import {
  getBrandCodeForHost,
  type RegionalBrandCode,
} from '@/lib/seo/marketUrls'
import {
  regionalBrandDefaultCountryIso,
  storefrontCurrencyIdForCode,
} from '@/lib/checkout/regionalStoreDefaults'
import { isEuropeanCountry } from '@/lib/middleware/variantRedirect.helper'

type StorefrontBrand = RegionalBrandCode | string | null | undefined

export type ResolveStorefrontMarketArgs = {
  /** Raw browser/request host. When present, this is the authoritative market signal. */
  host?: string | null
  /** Already-normalized host brand. Use only for the current host, not cookies. */
  hostBrand?: StorefrontBrand
  /** Route, cookie, or other non-host brand signal used only when the host is unknown. */
  fallbackBrand?: StorefrontBrand
  currencyCode?: string | null
  currencySymbol?: string | null
  cartCurrencyCode?: string | null
  cartCurrencySymbol?: string | null
  cartCountryIsoCode?: string | null
  cookieCountryIsoCode?: string | null
  cachedCountryIsoCode?: string | null
}

const symbolByCurrencyCode: Record<string, string> = {
  GBP: '£',
  USD: '$',
}

export const normalizeRegionalBrandCode = (
  value: StorefrontBrand
): RegionalBrandCode | null => {
  if (value === 'VANISHING_INC_UK' || value === 'VANISHING_INC_US') {
    return value
  }

  return null
}

export const normalizeCurrencyCode = (value: string | null | undefined) =>
  value?.trim().toUpperCase() || null

export const normalizeCountryIsoCode = (value: string | null | undefined) => {
  const code = value?.trim().toUpperCase()
  if (!code) return null
  return code === 'UK' ? 'GB' : code
}

const defaultCurrencyForBrand = (brand: RegionalBrandCode | null) => {
  if (brand === 'VANISHING_INC_UK') {
    return { currencyCode: 'GBP', currencySymbol: '£' }
  }
  if (brand === 'VANISHING_INC_US') {
    return { currencyCode: 'USD', currencySymbol: '$' }
  }
  return null
}

const countryMatchesBrand = (
  countryIsoCode: string,
  brand: RegionalBrandCode | null
) => {
  if (!brand) return true
  return brand === 'VANISHING_INC_UK'
    ? isEuropeanCountry(countryIsoCode)
    : !isEuropeanCountry(countryIsoCode)
}

export const resolveStorefrontMarket = ({
  host,
  hostBrand,
  fallbackBrand,
  currencyCode,
  currencySymbol,
  cartCurrencyCode,
  cartCurrencySymbol,
  cartCountryIsoCode,
  cookieCountryIsoCode,
  cachedCountryIsoCode,
}: ResolveStorefrontMarketArgs) => {
  const authoritativeHostBrand =
    (host ? getBrandCodeForHost(host) : null) ||
    normalizeRegionalBrandCode(hostBrand)
  const fallbackBrandCode = normalizeRegionalBrandCode(fallbackBrand)
  const brandCode = authoritativeHostBrand || fallbackBrandCode
  const storefrontDefault = defaultCurrencyForBrand(brandCode)
  const explicitCurrencyCode = normalizeCurrencyCode(currencyCode)
  const explicitCurrencySymbol = currencySymbol?.trim() || null
  const normalizedCartCurrencyCode = normalizeCurrencyCode(cartCurrencyCode)

  const resolvedCurrencyCode =
    storefrontDefault?.currencyCode ||
    explicitCurrencyCode ||
    normalizedCartCurrencyCode ||
    'USD'
  const resolvedCurrencySymbol =
    symbolByCurrencyCode[resolvedCurrencyCode] ||
    storefrontDefault?.currencySymbol ||
    explicitCurrencySymbol ||
    cartCurrencySymbol?.trim() ||
    '$'

  const countryIsoCode =
    [cartCountryIsoCode, cookieCountryIsoCode, cachedCountryIsoCode]
      .map(normalizeCountryIsoCode)
      .find(country => country && countryMatchesBrand(country, brandCode)) ||
    (brandCode ? regionalBrandDefaultCountryIso(brandCode) : 'US')

  return {
    brandCode,
    currencyId: storefrontCurrencyIdForCode(resolvedCurrencyCode),
    currencyCode: resolvedCurrencyCode,
    currencySymbol: resolvedCurrencySymbol,
    countryIsoCode,
  }
}

export const isGbpDisplayCurrency = (currencyCode: string | null | undefined) =>
  normalizeCurrencyCode(currencyCode) === 'GBP'
